import { useState, type ReactNode, type FormEvent } from "react";

export function PageHeader({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <h1 className="text-2xl font-extrabold tracking-tight">{title}</h1>
      {action}
    </div>
  );
}

export function Button({ children, onClick, type = "button", disabled }: {
  children: ReactNode; onClick?: () => void; type?: "button" | "submit"; disabled?: boolean;
}) {
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      className="rounded-xl bg-ink px-4 py-2.5 text-sm font-bold text-white hover:bg-black disabled:opacity-60 transition-colors">
      {children}
    </button>
  );
}

export function Card({ children }: { children: ReactNode }) {
  return <div className="bg-paper-card rounded-2xl border border-black/[0.07] shadow-card overflow-hidden">{children}</div>;
}

export function Table<T>({ columns, rows, empty }: {
  columns: { header: string; cell: (row: T) => ReactNode }[];
  rows: T[];
  empty?: string;
}) {
  return (
    <Card>
      <table className="w-full text-sm">
        <thead className="text-left text-ink-muted border-b border-black/[0.06]">
          <tr>{columns.map((c, i) => <th key={i} className="px-4 py-3 font-semibold">{c.header}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-black/[0.05]">
          {rows.map((row, i) => (
            <tr key={i} className="hover:bg-black/[0.015]">{columns.map((c, j) => <td key={j} className="px-4 py-3.5">{c.cell(row)}</td>)}</tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-ink-muted">{empty ?? "Nothing here yet."}</td></tr>
          )}
        </tbody>
      </table>
    </Card>
  );
}

export function Modal({ title, open, onClose, children }: {
  title: string; open: boolean; onClose: () => void; children: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-paper-card rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-extrabold tracking-tight">{title}</div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block mb-3">
      <span className="block text-sm font-semibold mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props}
    className="w-full rounded-xl border border-black/10 px-3.5 py-2.5 text-sm focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/15" />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props}
    className="w-full rounded-xl border border-black/10 px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/15" />;
}

/** Small helper to manage a create-form modal + submit + error. */
export function useForm<T extends Record<string, any>>(initial: T) {
  const [values, setValues] = useState<T>(initial);
  const set = (k: keyof T, v: any) => setValues((s) => ({ ...s, [k]: v }));
  const reset = () => setValues(initial);
  return { values, set, reset, setValues };
}

export function FormError({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = (error as any)?.message ?? "Something went wrong.";
  return <div className="mb-3 text-sm text-danger">{msg}</div>;
}

export function FormShell({ onSubmit, children }: { onSubmit: (e: FormEvent) => void; children: ReactNode }) {
  return <form onSubmit={onSubmit}>{children}</form>;
}

/* ── Loading skeletons ────────────────────────────────────────────── */
function Bar({ w = "100%", h = 14 }: { w?: string; h?: number }) {
  return <div className="rounded bg-black/[0.07] animate-pulse" style={{ width: w, height: h }} />;
}

export function TableSkeleton({ cols = 5, rows = 5 }: { cols?: number; rows?: number }) {
  return (
    <Card>
      <div className="px-4 py-3.5 border-b border-black/[0.06] flex gap-6">
        {Array.from({ length: cols }).map((_, i) => <Bar key={i} w="70px" h={12} />)}
      </div>
      <div className="divide-y divide-black/[0.05]">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="px-4 py-4 flex gap-6 items-center">
            {Array.from({ length: cols }).map((_, c) => <Bar key={c} w={c === 0 ? "38%" : "64px"} />)}
          </div>
        ))}
      </div>
    </Card>
  );
}

export function StatsSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-paper-card rounded-2xl border border-black/[0.07] shadow-card p-5 space-y-3">
          <Bar w="45%" h={12} /><Bar w="55%" h={28} /><Bar w="50%" h={12} />
        </div>
      ))}
    </div>
  );
}

export function CardSkeleton({ lines = 4 }: { lines?: number }) {
  return (
    <Card>
      <div className="p-5 space-y-3">
        {Array.from({ length: lines }).map((_, i) => <Bar key={i} w={`${90 - i * 12}%`} />)}
      </div>
    </Card>
  );
}
